import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import SearchHighlightBanner from './components/SearchHighlightBanner';
import ReferencePagesBanner from './components/ReferencePagesBanner';
import { useToast } from './ToastContext';
import Card from './components/Card';
import Badge from './components/Badge';
import Button from './components/Button';
import Loader from './components/Loader';
import {
  Lightbulb, Building2, TrendingUp, AlertTriangle, MessageCircle, Star,
  Sparkles, RefreshCw, CheckCircle2, Info, FileText,
  Download, Copy, MapPin, Users, HelpCircle, Zap, BookOpen, Globe,
  Brain, Target, Shield, Search, BarChart3, ChevronRight, ExternalLink,
  AlertCircle
} from 'lucide-react';
import usePositioningAnalysis from './hooks/usePositioningAnalysis';
import useFilesVerification from './hooks/useFilesVerification';
import decodeHtml from './utils/decodeHtml';
import { generateLlmTxt, generateSchemaOrg, generateRichSnippets } from './utils/fileGeneration';
import { getAIVisibility, getLLMVisibilityTest } from './api';

const getStr = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.titre || v.question || v.tache || v.kw || JSON.stringify(v);
  return String(v);
};

// ── Sub-components ─────────────────────────────────────────────────────────

function GeoScoreRing({ score, size = 96 }) {
  const n = Math.round(score || 0);
  const r = 38; const circ = 2 * Math.PI * r;
  const fill = circ * (1 - n / 100);
  const color = n >= 70 ? '#22c55e' : n >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={fill}
          strokeLinecap="round" transform="rotate(-90 50 50)" />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
          fontSize="20" fontWeight="bold" fill={color}>{n}</text>
      </svg>
      <span className="text-xs text-blue-gray-400">Score GEO</span>
    </div>
  );
}

function DimBar({ label, score, icon: Icon }) {
  const n = Math.round(score || 0);
  const color = n >= 70 ? 'bg-green-500' : n >= 50 ? 'bg-amber-500' : 'bg-red-400';
  const textColor = n >= 70 ? 'text-green-700' : n >= 50 ? 'text-amber-700' : 'text-red-600';
  return (
    <div className="flex items-center gap-3">
      {Icon && <Icon size={14} className="text-blue-gray-400 shrink-0" />}
      <span className="text-sm text-blue-gray-600 w-40 shrink-0">{label}</span>
      <div className="flex-1 bg-blue-gray-100 rounded-full h-2">
        <div className={`h-2 rounded-full transition-all duration-500 ${color}`} style={{ width: `${n}%` }} />
      </div>
      <span className={`text-sm font-bold w-10 text-right ${textColor}`}>{n}</span>
    </div>
  );
}

function AlertBanner({ titre, description, urgence }) {
  const colors = {
    critique: 'bg-red-50 border-red-200 text-red-800',
    haute:    'bg-amber-50 border-amber-200 text-amber-800',
    moyenne:  'bg-blue-50 border-blue-200 text-blue-800',
  };
  const icons = { critique: AlertCircle, haute: AlertTriangle, moyenne: Info };
  const C = colors[urgence] || colors.moyenne;
  const I = icons[urgence] || Info;
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${C}`}>
      <I size={16} className="shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold text-sm">{titre}</span>
        {description && <p className="text-xs mt-0.5 opacity-80">{description}</p>}
      </div>
    </div>
  );
}

function TokenCost({ usage }) {
  if (!usage || !usage.total) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-blue-gray-400 bg-blue-gray-50 rounded-lg px-3 py-2 border border-blue-gray-100">
      <Zap size={12} className="text-accent" />
      <span>{usage.total?.toLocaleString()} tokens</span>
      <span className="text-blue-gray-300">·</span>
      <span>≈ {usage.estimated_cost_eur?.toFixed(5)}€</span>
    </div>
  );
}

function AnalyseSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {[96, 64, 80, 48].map((h, i) => (
        <div key={i} className={`h-${h > 80 ? 24 : h > 60 ? 16 : 12} bg-neutral-100 rounded-xl`} style={{ height: h }} />
      ))}
    </div>
  );
}

// ── Tab: Vue d'ensemble ────────────────────────────────────────────────────

function TabOverview({ iaData, setActiveTab }) {
  const raw = iaData?.raw_json || {};
  const geoScore = raw.geo_score || {};
  const identite = raw.identite || {};
  const actionPlan = raw.action_plan || {};
  const concurrence = raw.concurrence || {};
  
  const globalScore = geoScore.score_global_geo || 0;
  const dims = geoScore.dimensions || {};
  const alertes = geoScore.alertes_prioritaires || [];
  const todoList = actionPlan.todo_list || iaData?.todo_list || [];
  const concurrents = concurrence.concurrents || iaData?.concurrents || [];

  const dimConfig = [
    { key: 'signaux',       label: 'Signaux structurés',    icon: Shield },
    { key: 'contenu_llm',   label: 'Contenu LLM',           icon: FileText },
    { key: 'autorite',      label: 'Autorité & E-E-A-T',    icon: Star },
    { key: 'accessibilite_ia', label: 'Accessibilité IA',   icon: Globe },
  ];

  return (
    <div className="space-y-6">
      {/* Banner / Global Score */}
      <div 
        onClick={() => setActiveTab?.('scores')}
        className="rounded-2xl border border-neutral-200/80 bg-white p-6 sm:p-8 hover:border-accent/40 hover:shadow-md cursor-pointer transition-all flex flex-col sm:flex-row items-center gap-8 relative overflow-hidden group"
      >
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="text-accent" />
        </div>
        <GeoScoreRing score={globalScore} />
        <div className="flex-1 space-y-4 w-full">
          <div>
            <h3 className="text-lg font-bold text-blue-gray-900">Scores de Visibilité IA</h3>
            <p className="text-sm text-blue-gray-500">Capacité des moteurs génératifs à analyser et recommander ce site.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dimConfig.map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between bg-neutral-50 px-3 py-2 rounded-lg border border-neutral-100/50">
                <div className="flex items-center gap-2">
                  <Icon size={14} className="text-blue-gray-500" />
                  <span className="text-sm text-blue-gray-800">{label}</span>
                </div>
                <span className="font-semibold text-blue-gray-900">{dims[key]?.score ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Identité */}
        <div 
          onClick={() => setActiveTab?.('identite')}
          className="rounded-xl border border-neutral-200 bg-white p-5 hover:border-accent/40 cursor-pointer transition-all flex flex-col group h-full"
        >
          <div className="flex items-center justify-between mb-3 text-blue-gray-900">
            <div className="flex items-center gap-2">
              <BookOpen size={16} className="text-accent" />
              <span className="font-semibold">Identité & Cible</span>
            </div>
            <ChevronRight size={16} className="text-neutral-300 group-hover:text-accent transition-colors" />
          </div>
          <p className="text-xs text-blue-gray-600 line-clamp-3 leading-relaxed mb-3 flex-1">
            {identite.comprehension || "L'IA analyse le positionnement de la marque et les intentions de recherche."}
          </p>
          <div className="mt-auto">
             <span className="text-[11px] font-medium text-blue-gray-500 bg-blue-gray-50 px-2 py-1.5 rounded-md">Voir les mots-clés</span>
          </div>
        </div>

        {/* Action Plan */}
        <div 
          onClick={() => setActiveTab?.('action')}
          className="rounded-xl border border-neutral-200 bg-white p-5 hover:border-accent/40 cursor-pointer transition-all flex flex-col group h-full"
        >
          <div className="flex items-center justify-between mb-3 text-blue-gray-900">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-amber-500" />
              <span className="font-semibold">Plan d'action</span>
            </div>
            <ChevronRight size={16} className="text-neutral-300 group-hover:text-amber-500 transition-colors" />
          </div>
          <p className="text-xs text-blue-gray-600 leading-relaxed mb-4 flex-1">
             <strong className="text-blue-gray-900">{todoList.length} actions</strong> d'optimisation GEO et <strong className="text-blue-gray-900">{alertes.length} alertes</strong> détectées.
          </p>
          <div className="mt-auto">
             {alertes.length > 0 ? (
               <Badge color="warning" size="sm">{alertes.length} alertes bloquantes</Badge>
             ) : (
               <Badge color="success" size="sm">Aucune alerte bloquante</Badge>
             )}
          </div>
        </div>

        {/* Concurrence */}
        <div 
          onClick={() => setActiveTab?.('concurrence')}
          className="rounded-xl border border-neutral-200 bg-white p-5 hover:border-accent/40 cursor-pointer transition-all flex flex-col group h-full"
        >
          <div className="flex items-center justify-between mb-3 text-blue-gray-900">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-indigo-500" />
              <span className="font-semibold">Concurrence LLM</span>
            </div>
            <ChevronRight size={16} className="text-neutral-300 group-hover:text-indigo-500 transition-colors" />
          </div>
          <p className="text-xs text-blue-gray-600 leading-relaxed mb-4 flex-1">
             <strong className="text-blue-gray-900">{concurrents.length} concurrents</strong> majeurs identifiés par l'intelligence artificielle dans ce secteur.
          </p>
          <div className="mt-auto">
             <span className="text-[11px] font-medium text-indigo-600 bg-indigo-50 px-2 py-1.5 rounded-md">Voir le match concurrentiel</span>
          </div>
        </div>
      </div>

      {raw.token_usage && (
        <div className="flex justify-end pt-2">
          <TokenCost usage={raw.token_usage} />
        </div>
      )}
    </div>
  );
}

// ── Tab: Identité & Mots-clés ─────────────────────────────────────────────

function TabIdentite({ iaData }) {
  const raw = iaData?.raw_json || {};
  const identite = raw.identite || {};
  const motsCles = identite.mots_cles || iaData?.mots_cles || [];
  const questionsClients = identite.questions_clients || [];
  const locationStr = [identite.localite, identite.localisation].filter(Boolean).join(', ') || '—';

  const intentionColors = {
    informationnel: 'bg-blue-100 text-blue-700',
    transactionnel:  'bg-green-100 text-green-700',
    local:           'bg-amber-100 text-amber-700',
  };

  return (
    <div className="space-y-6">
      {/* Identité 2×2 */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={16} className="text-accent" />
          <span className="font-semibold text-blue-gray-900 text-base">Identité du site</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { label: 'Secteur', value: identite.secteur || iaData?.secteur, icon: Building2 },
            { label: 'Localisation', value: locationStr, icon: MapPin },
            { label: 'Clientèle cible', value: identite.clientele || iaData?.clientele, icon: Users },
            { label: 'Proposition de valeur', value: identite.proposition_valeur || iaData?.notorie, icon: Star },
          ].map(({ label, value, icon: I }) => (
            <div key={label} className="bg-neutral-50 rounded-lg p-4 border border-neutral-100">
              <div className="flex items-center gap-2 mb-2">
                <I size={13} className="text-neutral-400" />
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{label}</span>
              </div>
              <p className="text-sm text-neutral-700">{decodeHtml(getStr(value)) || '—'}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Mots-clés avec intention */}
      {motsCles.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-accent" />
            <span className="font-semibold text-blue-gray-900 text-base">Mots-clés stratégiques</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {motsCles.map((m, i) => {
              const kw  = typeof m === 'object' ? m.kw : m;
              const int = typeof m === 'object' ? m.intention : null;
              return (
                <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium">
                  {decodeHtml(getStr(kw))}
                  {int && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${intentionColors[int] || 'bg-gray-100 text-gray-600'}`}>{int}</span>}
                </span>
              );
            })}
          </div>
        </Card>
      )}

      {/* Questions clients par intention */}
      {questionsClients.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle size={16} className="text-blue-500" />
            <span className="font-semibold text-blue-gray-900 text-base">Questions clients (voix LLM)</span>
          </div>
          <div className="space-y-2">
            {questionsClients.map((q, i) => {
              const question = typeof q === 'object' ? q.question : q;
              const int = typeof q === 'object' ? q.intention : null;
              return (
                <div key={i} className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                  <MessageCircle size={14} className="text-blue-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-blue-gray-700 flex-1">{decodeHtml(getStr(question))}</span>
                  {int && <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${intentionColors[int] || 'bg-gray-100 text-gray-600'}`}>{int}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Scores GEO ───────────────────────────────────────────────────────

const GEO_DIM_INFO = {
  signaux: {
    label: 'Signaux structurés',
    icon: Shield,
    color: 'indigo',
    explication: 'Capacité des LLM à extraire vos données via schema.org et JSON-LD. Un site bien balisé est plus souvent cité directement par les IA.'
  },
  contenu_llm: {
    label: 'Contenu exploitable LLM',
    icon: FileText,
    color: 'blue',
    explication: 'Richesse textuelle et structure adaptée aux réponses génératives : FAQ, prose dense, H2/H3 logiques. Les LLM puisent dans votre contenu pour formuler leurs réponses.'
  },
  autorite: {
    label: 'Autorité & E-E-A-T',
    icon: Star,
    color: 'amber',
    explication: 'Signaux de confiance que les LLM utilisent pour vous citer : auteur identifié, date de publication, sources claires, expertise démontrée.'
  },
  accessibilite_ia: {
    label: 'Accessibilité IA',
    icon: Globe,
    color: 'green',
    explication: "Degré d'accès des crawlers LLM à votre contenu : fichier llm.txt, robots.txt permissif, contenu accessible sans JavaScript."
  },
};

function DimensionCard({ dimKey, dim }) {
  const [showTip, setShowTip] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const info = GEO_DIM_INFO[dimKey] || {};
  const Icon = info.icon || Shield;
  const score = dim?.score || 0;
  const scoreColor = score >= 70 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-500';
  const bgBar   = score >= 70 ? 'bg-green-500'  : score >= 50 ? 'bg-amber-500'  : 'bg-red-400';

  return (
    <div className={`rounded-xl border bg-white p-4 sm:p-5 space-y-4 transition-all duration-300 ${isExpanded ? 'border-neutral-200/80 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]' : 'border-neutral-200/50 hover:border-neutral-300/80 hover:shadow-sm'}`}>
      {/* Clickable Header */}
      <div 
        className="flex items-start sm:items-center justify-between gap-4 cursor-pointer group"
        onClick={() => setIsExpanded(v => !v)}
      >
        <div className="flex items-start sm:items-center gap-3 flex-1">
          <div className="p-2 rounded-lg bg-neutral-100/80 shrink-0 group-hover:bg-neutral-200/60 transition-colors">
            <Icon size={18} className="text-blue-gray-700" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-blue-gray-900 text-base group-hover:text-accent transition-colors">{info.label}</span>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowTip(v => !v); }} 
                className="text-blue-gray-300 hover:text-accent transition-colors mt-0.5"
                title="Comprendre ce score"
              >
                <HelpCircle size={15} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="text-right">
            <span className={`text-2xl sm:text-3xl font-bold tracking-tight ${scoreColor}`}>{score}</span>
            <span className="text-sm text-blue-gray-400 font-medium ml-0.5">/100</span>
          </div>
          <ChevronRight size={20} className={`text-blue-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-90' : 'rotate-0'}`} />
        </div>
      </div>

      {showTip && (
        <p className="text-xs sm:text-sm text-blue-gray-600 leading-relaxed bg-blue-50/50 rounded-xl px-4 py-3 border border-blue-100/50 animate-in fade-in zoom-in-95">
          {info.explication}
        </p>
      )}

      {/* Progress bar */}
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-1000 ease-out ${bgBar}`} style={{ width: `${score}%` }} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="space-y-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
          {/* Points forts / faibles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {dim?.points_forts?.length > 0 && (
              <div className="bg-green-50/40 border border-green-100/60 rounded-2xl p-5">
                <div className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-1.5">
                  <CheckCircle2 size={16} /> Ce qui fonctionne bien
                </div>
                <ul className="space-y-3">
                  {dim.points_forts.map((p, i) => (
                    <li key={i} className="text-sm text-green-900 leading-relaxed flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2.5 shrink-0 shadow-sm" />
                      <span>{decodeHtml(p)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {dim?.points_faibles?.length > 0 && (
              <div className="bg-red-50/40 border border-red-100/60 rounded-2xl p-5">
                <div className="text-sm font-semibold text-red-700 mb-4 flex items-center gap-1.5">
                  <AlertTriangle size={16} /> Ce qui bloque l'IA
                </div>
                <ul className="space-y-3">
                  {dim.points_faibles.map((p, i) => (
                    <li key={i} className="text-sm text-red-900 leading-relaxed flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2.5 shrink-0 shadow-sm" />
                      <span>{decodeHtml(p)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Recommandations GEO */}
          {dim?.recommandations?.length > 0 && (
            <div className="mt-2 pt-6 border-t border-neutral-100">
              <div className="text-sm font-semibold text-blue-gray-800 mb-4 flex items-center gap-2">
                <Zap size={16} className="text-amber-500" />
                Actions prioritaires suggérées
              </div>
              <div className="space-y-2.5">
                {dim.recommandations.map((r, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-neutral-50/80 hover:bg-neutral-100 transition-colors rounded-xl px-4 py-3.5 border border-neutral-200/50">
                    <div className="flex items-start sm:items-center gap-3 flex-1">
                      <ChevronRight size={16} className="text-neutral-400 shrink-0 mt-0.5 sm:mt-0" />
                      <span className="text-sm text-blue-gray-700 leading-relaxed">{getStr(r.action || r)}</span>
                    </div>
                    {r.impact && (
                      <Badge color={r.impact === 'haute' ? 'error' : r.impact === 'moyenne' ? 'warning' : 'gray'} size="sm" className="w-fit sm:w-auto mt-1 sm:mt-0 shadow-sm">
                        Impact {r.impact}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabScoresGeo({ iaData, slug, showToast }) {
  const [tests, setTests] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testUsage, setTestUsage] = useState(null);

  const raw    = iaData?.raw_json || {};
  const dims   = (raw.geo_score || {}).dimensions || {};
  const dimKeys = ['signaux', 'contenu_llm', 'autorite', 'accessibilite_ia'];

  const handleRunTest = async () => {
    setTestLoading(true); setTestError(null);
    try {
      const res = await getLLMVisibilityTest(slug);
      setTests(res.tests); setTestUsage(res.token_usage);
    } catch (e) { setTestError(e.response?.data?.detail || e.message || 'Erreur'); }
    finally { setTestLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Intro */}
      <div className="bg-accent/5 border border-accent/20 rounded-xl px-4 py-3 flex items-start gap-3">
        <Shield size={16} className="text-accent mt-0.5 shrink-0" />
        <p className="text-sm text-blue-gray-700">
          Ces 4 dimensions mesurent la capacité des moteurs génératifs (ChatGPT, Gemini, Perplexity…) à trouver, comprendre et citer votre site.
          Cliquez sur <HelpCircle size={13} className="inline" /> pour comprendre chaque score.
        </p>
      </div>

      {/* 4 dimension cards */}
      {dimKeys.map(key => (
        <DimensionCard key={key} dimKey={key} dim={dims[key]} />
      ))}

      {/* Test de visibilité LLM */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain size={16} className="text-indigo-500" />
            <span className="font-semibold text-blue-gray-900">Test de visibilité LLM</span>
          </div>
          <div className="flex items-center gap-3">
            {testUsage && <TokenCost usage={testUsage} />}
            <Button size="sm" variant="secondary" onClick={handleRunTest} disabled={testLoading}>
              <RefreshCw size={13} className={`mr-1.5 ${testLoading ? 'animate-spin' : ''}`} />
              {testLoading ? 'Simulation...' : 'Lancer les 3 tests'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-blue-gray-400 mb-4">Simule 3 requêtes conversationnelles et vérifie si votre site est mentionné dans les réponses d'un LLM.</p>
        {testError && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">{testError}</div>}
        {tests ? (
          <div className="space-y-3">
            {tests.map((t, i) => (
              <div key={i} className={`rounded-lg border p-4 ${t.site_mentioned ? 'bg-green-50/50 border-green-200' : 'bg-neutral-50 border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                    t.intention === 'informationnel' ? 'bg-blue-100 text-blue-700' :
                    t.intention === 'transactionnel' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>{t.intention}</span>
                  {t.site_mentioned
                    ? <span className="flex items-center gap-1 text-green-700 text-xs font-medium"><CheckCircle2 size={13} /> Site mentionné ✓</span>
                    : <span className="text-xs text-blue-gray-400">Site non mentionné</span>}
                </div>
                <p className="text-xs text-blue-gray-500 italic mb-2">« {t.question} »</p>
                {t.response && <p className="text-sm text-blue-gray-700 leading-relaxed">{t.response}</p>}
              </div>
            ))}
          </div>
        ) : !testLoading && (
          <div className="text-center py-6 text-blue-gray-300">
            <Sparkles size={24} className="mx-auto mb-2" />
            <p className="text-sm">Cliquez sur "Lancer les 3 tests" pour tester la visibilité de votre site </p>
          </div>
        )}
      </Card>
    </div>
  );
}


// ── Tab: Concurrence ───────────────────────────────────────────────────────

function TabConcurrence({ iaData }) {
  const raw = iaData?.raw_json || {};
  const identite = raw.identite || {};
  const concurrence = raw.concurrence || {};
  const concurrents = concurrence.concurrents || iaData?.concurrents || [];
  const positionnement = concurrence.positionnement_relatif || '';
  const opportunites = concurrence.opportunites || [];
  const secteurUtilise = identite.secteur || null;
  const localiteUtilisee = identite.localite || identite.localisation || null;

  const fiabiliteColors = { haute: 'success', moyenne: 'warning', basse: 'gray' };

  return (
    <div className="space-y-6">
      {/* Avertissement fiabilité */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Données basées sur la connaissance interne de Gemini</span> — vérifiez toujours la pertinence réelle des concurrents affichés.
          {secteurUtilise && (
            <span className="block mt-1 text-xs text-amber-700">
              Secteur analysé : <strong>{secteurUtilise}</strong>{localiteUtilisee ? ` · Zone : ${localiteUtilisee}` : ''}
            </span>
          )}
        </div>
      </div>

      {positionnement && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Target size={15} className="text-accent" />
            <span className="font-semibold text-blue-gray-900">Positionnement relatif</span>
          </div>
          <p className="text-sm text-blue-gray-700 leading-relaxed">{decodeHtml(positionnement)}</p>
          {opportunites.length > 0 && (
            <div className="mt-4 pt-3 border-t border-neutral-100">
              <div className="text-xs font-semibold text-accent mb-2">Opportunités de différenciation GEO</div>
              <ul className="space-y-1.5">{opportunites.map((o, i) => (
                <li key={i} className="text-sm text-blue-gray-700 flex items-start gap-2">
                  <ChevronRight size={13} className="text-accent mt-0.5 shrink-0" />{decodeHtml(o)}
                </li>
              ))}</ul>
            </div>
          )}
        </Card>
      )}

      {concurrents.length > 0 ? (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={15} className="text-accent" />
            <span className="font-semibold text-blue-gray-900 text-base">Concurrents identifiés</span>
            <span className="text-xs text-blue-gray-400">({concurrents.length})</span>
          </div>
          <div className="space-y-3">
            {concurrents.map((c, i) => {
              const nom  = typeof c === 'string' ? c : (c.nom || c.name || '');
              const desc = typeof c === 'string' ? '' : (c.description || '');
              const pourquoi = typeof c === 'object' ? c.pourquoi_concurrent : null;
              const angle = typeof c === 'object' ? c.angle_differenciant : null;
              const fiab  = typeof c === 'object' ? c.fiabilite : null;
              return (
                <div key={i} className="rounded-xl border border-neutral-200 p-4 hover:border-accent/30 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-blue-gray-900">{decodeHtml(nom)}</span>
                    {fiab && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-blue-gray-400">Fiabilité :</span>
                        <Badge color={fiabiliteColors[fiab] || 'gray'} size="sm">{fiab}</Badge>
                      </div>
                    )}
                  </div>
                  {desc && <p className="text-sm text-blue-gray-600 mb-2">{decodeHtml(desc)}</p>}
                  {pourquoi && <div className="text-xs text-blue-gray-500 bg-blue-gray-50 rounded px-2 py-1.5 mb-2">🎯 {decodeHtml(pourquoi)}</div>}
                  {angle && <div className="text-xs text-green-700 bg-green-50 rounded px-2 py-1.5">💡 Angle GEO différenciant : {decodeHtml(angle)}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      ) : (
        <Card><p className="text-sm text-blue-gray-400 text-center py-8">Aucun concurrent identifié — relancez l'analyse GEO.</p></Card>
      )}
    </div>
  );
}

// ── Tab: Plan d'action ─────────────────────────────────────────────────────

function TabAction({ iaData, slug }) {
  const raw = iaData?.raw_json || {};
  const actionPlan = raw.action_plan || {};
  const todoList = actionPlan.todo_list || iaData?.todo_list || [];
  const idees = actionPlan.idees_contenu_geo || [];

  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`geo-todo-${slug}`) || '[]')); } catch { return new Set(); }
  });
  const [filterPriority, setFilterPriority] = useState('all');

  const toggleDone = (i) => {
    setDone(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      localStorage.setItem(`geo-todo-${slug}`, JSON.stringify([...next]));
      return next;
    });
  };

  const priorityColors = { haute: 'bg-red-100 text-red-700', moyenne: 'bg-amber-100 text-amber-700', basse: 'bg-blue-gray-100 text-blue-gray-600' };
  const intentionColors = { informationnel: 'bg-blue-100 text-blue-700', transactionnel: 'bg-green-100 text-green-700', local: 'bg-amber-100 text-amber-700' };

  const filteredTodo = todoList.filter(t =>
    filterPriority === 'all' || (typeof t === 'object' ? t.priorite : '') === filterPriority
  );
  const doneCount = todoList.filter((_, i) => done.has(i)).length;

  return (
    <div className="space-y-6">
      {/* Intro GEO */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <Brain size={16} className="text-indigo-500 mt-0.5 shrink-0" />
        <p className="text-sm text-indigo-800">
          Ces recommandations visent à améliorer votre <strong>visibilité dans les moteurs génératifs</strong> (ChatGPT, Perplexity, Gemini, Claude…) — pas votre référencement Google classique.
        </p>
      </div>

      {/* Todo list GEO */}
      {filteredTodo.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap size={15} className="text-amber-500" />
              <span className="font-semibold text-blue-gray-900 text-base">Plan d'action GEO</span>
              <span className="text-xs text-blue-gray-400">{doneCount}/{todoList.length} complétées</span>
            </div>
            <div className="flex gap-1">
              {['all', 'haute', 'moyenne', 'basse'].map(p => (
                <button key={p} onClick={() => setFilterPriority(p)}
                  className={`text-xs px-2 py-1 rounded-md transition-colors ${filterPriority === p ? 'bg-accent text-white' : 'bg-blue-gray-100 text-blue-gray-600 hover:bg-blue-gray-200'}`}>
                  {p === 'all' ? 'Toutes' : p}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {filteredTodo.map((t, i) => {
              const tache = typeof t === 'object' ? t.tache : t;
              const prio  = typeof t === 'object' ? t.priorite : null;
              const cat   = typeof t === 'object' ? t.categorie : null;
              const impact = typeof t === 'object' ? t.impact_estime : null;
              const isDone = done.has(i);
              return (
                <div key={i}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-all cursor-pointer ${isDone ? 'bg-green-50/50 border-green-200 opacity-70' : 'bg-white border-blue-gray-200 hover:border-accent/30'}`}
                  onClick={() => toggleDone(i)}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${isDone ? 'bg-green-500 border-green-500' : 'border-blue-gray-300'}`}>
                    {isDone && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <span className={`text-sm ${isDone ? 'line-through text-blue-gray-400' : 'text-blue-gray-800'}`}>{decodeHtml(getStr(tache))}</span>
                    {impact && !isDone && <p className="text-xs text-blue-gray-400 mt-0.5">{decodeHtml(impact)}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {cat && <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded font-medium">{cat}</span>}
                    {prio && <span className={`text-[10px] px-1.5 py-0.5 rounded ${priorityColors[prio] || ''}`}>{prio}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Idées de contenu GEO */}
      {idees.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={15} className="text-yellow-500" />
            <span className="font-semibold text-blue-gray-900 text-base">Idées de contenu GEO</span>
          </div>
          <p className="text-xs text-blue-gray-400 mb-3">Contenus pensés pour répondre aux requêtes conversationnelles et être cité par les IA.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {idees.map((idea, i) => {
              const titre = typeof idea === 'object' ? idea.titre : idea;
              const type  = typeof idea === 'object' ? idea.type : null;
              const int   = typeof idea === 'object' ? idea.intention : null;
              const angle = typeof idea === 'object' ? idea.angle : null;
              return (
                <div key={i} className="rounded-lg border border-yellow-100 bg-yellow-50/40 p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    {type && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">{type}</span>}
                    {int  && <span className={`text-[10px] px-1.5 py-0.5 rounded ${intentionColors[int] || 'bg-gray-100 text-gray-600'}`}>{int}</span>}
                  </div>
                  <p className="text-sm font-medium text-blue-gray-800">{decodeHtml(getStr(titre))}</p>
                  {angle && <p className="text-xs text-blue-gray-500 mt-1 leading-relaxed">{decodeHtml(angle)}</p>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Ressources ─────────────────────────────────────────────────────────


function TabRessources({ iaData, project, slug, showToast }) {
  const [llmTxt, setLlmTxt]     = useState(() => localStorage.getItem(`file_${slug}_llm`) || '');
  const [schemaTxt, setSchemaTxt] = useState(() => localStorage.getItem(`file_${slug}_schema`) || '');
  const [richTxt, setRichTxt]   = useState(() => localStorage.getItem(`file_${slug}_rich`) || '');
  const [fileTab, setFileTab]   = useState('llm');
  const [generating, setGenerating] = useState(false);
  const [liveContent, setLiveContent] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const { filesStatus, checkFilesOnSite } = useFilesVerification(slug);

  const [editedTemplateLlm, setEditedTemplateLlm] = useState('');
  const [editedSchemaRec, setEditedSchemaRec] = useState('');

  useEffect(() => {
    const rw = iaData?.raw_json || {};
    const ap = rw.action_plan || {};
    setEditedTemplateLlm(ap.template_llm_txt || '');
    const rec = ap.schema_recommande;
    setEditedSchemaRec(typeof rec === 'object' ? JSON.stringify(rec, null, 2) : (rec || ''));
  }, [iaData]);

  const handleGenerate = async () => {
    if (!project || !iaData) return;
    setGenerating(true);
    try {
      const l = generateLlmTxt(project, iaData); setLlmTxt(l); localStorage.setItem(`file_${slug}_llm`, l);
      const s = generateSchemaOrg(project, iaData); setSchemaTxt(s); localStorage.setItem(`file_${slug}_schema`, s);
      const r = generateRichSnippets(project, iaData); setRichTxt(r); localStorage.setItem(`file_${slug}_rich`, r);
    } finally { setGenerating(false); }
  };

  const handleCheckAndFetch = async () => {
    setLiveLoading(true);
    setLiveContent(null);
    await checkFilesOnSite();
    // Try fetching llm.txt live from site
    try {
      const siteUrl = project?.url?.replace(/\/$/, '');
      if (siteUrl) {
        const res = await fetch(`${siteUrl}/llm.txt`, { mode: 'no-cors' });
        // no-cors won't give us the body, try a cors-friendly approach via proxy if available
      }
    } catch { /* ignore */ }
    setLiveLoading(false);
  };

  const download = (content, name) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
    a.download = name; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const copy = (text) => { navigator.clipboard.writeText(text).then(() => showToast('Copié !', 'success')); };

  const currentContent  = fileTab === 'llm' ? llmTxt : fileTab === 'schema' ? schemaTxt : richTxt;
  const currentFilename = fileTab === 'llm' ? 'llm.txt' : fileTab === 'schema' ? 'schema.json' : 'snippets.json';

  return (
    <div className="space-y-6">
      {/* Statut des fichiers sur le site */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe size={15} className="text-accent" />
            <span className="font-semibold text-blue-gray-900 text-base">Fichiers GEO sur votre site</span>
          </div>
          <Button size="sm" variant="secondary" onClick={checkFilesOnSite} disabled={liveLoading}>
            <RefreshCw size={13} className={`mr-1.5 ${liveLoading ? 'animate-spin' : ''}`} /> Vérifier
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* llm.txt */}
          <div className={`rounded-lg border p-4 ${filesStatus.llm.exists ? 'border-green-200 bg-green-50/50' : 'border-neutral-200 bg-neutral-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-gray-800">llm.txt</span>
              <Badge color={filesStatus.llm.exists ? 'success' : 'gray'}>{filesStatus.llm.exists ? '✓ Présent' : 'Absent'}</Badge>
            </div>
            <p className="text-xs text-blue-gray-500">Fichier d'instructions pour les crawlers LLM. Améliore la façon dont les IA interprètent votre site.</p>
          </div>
          {/* schema.org */}
          <div className={`rounded-lg border p-4 ${filesStatus.schema.exists ? 'border-green-200 bg-green-50/50' : 'border-neutral-200 bg-neutral-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-gray-800">Schema.org / JSON-LD</span>
              <Badge color={filesStatus.schema.exists ? 'success' : 'gray'}>{filesStatus.schema.exists ? '✓ Détecté' : 'Non détecté'}</Badge>
            </div>
            <p className="text-xs text-blue-gray-500">Données structurées permettant aux LLM d'extraire des informations précises sur votre site.</p>
          </div>
        </div>
      </Card>

      {/* Générateur de fichiers */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={15} className="text-accent" />
            <span className="font-semibold text-blue-gray-900 text-base">Générateur de fichiers GEO</span>
          </div>
          <Button size="sm" onClick={handleGenerate} disabled={generating || !iaData}>
            <RefreshCw size={13} className={`mr-1.5 ${generating ? 'animate-spin' : ''}`} />Générer
          </Button>
        </div>
        <div className="flex gap-1 bg-neutral-100 p-1 rounded-lg mb-4 w-fit">
          {[['llm', 'llm.txt'], ['schema', 'schema.org'], ['rich', 'Rich Snippets']].map(([k, l]) => (
            <button key={k} onClick={() => setFileTab(k)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${fileTab === k ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}>{l}</button>
          ))}
        </div>
        <textarea
          className="w-full text-sm font-mono bg-neutral-50 rounded-lg border border-neutral-200 p-4 min-h-[250px] resize-y focus:outline-none focus:ring-1 focus:ring-accent text-blue-gray-700"
          value={currentContent}
          onChange={(e) => {
            const val = e.target.value;
            if (fileTab === 'llm')      { setLlmTxt(val);   localStorage.setItem(`file_${slug}_llm`,   val); }
            else if (fileTab === 'schema'){ setSchemaTxt(val); localStorage.setItem(`file_${slug}_schema`, val); }
            else                        { setRichTxt(val);   localStorage.setItem(`file_${slug}_rich`,   val); }
          }}
          placeholder='Cliquez sur "Générer" pour créer ce fichier à partir de votre analyse GEO'
        />
        <div className="flex gap-2 mt-3 justify-end">
          <Button size="sm" variant="secondary" onClick={() => copy(currentContent)} disabled={!currentContent}>
            <Copy size={13} className="mr-1.5" /> Copier
          </Button>
          <Button size="sm" onClick={() => download(currentContent, currentFilename)} disabled={!currentContent}>
            <Download size={13} className="mr-1.5" /> Télécharger
          </Button>
        </div>
      </Card>

      {/* Recommandations Gemini pour llm.txt et schema */}
      {/* Recommandations Gemini pour llm.txt et schema */}
      {(editedTemplateLlm || editedSchemaRec) && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={15} className="text-accent" />
            <span className="font-semibold text-blue-gray-900 text-base">Recommandations GEO personnalisées</span>
          </div>
          {editedTemplateLlm && (
            <div className="mb-6">
              <div className="text-xs font-semibold text-blue-gray-600 mb-2">Contenu recommandé pour llm.txt</div>
              <textarea 
                className="w-full text-xs font-mono bg-neutral-50 rounded-lg border border-neutral-200 p-4 min-h-[150px] resize-y focus:outline-none focus:ring-1 focus:ring-accent text-blue-gray-700"
                value={editedTemplateLlm}
                onChange={(e) => setEditedTemplateLlm(e.target.value)}
              />
              <Button size="sm" variant="secondary" className="mt-2" onClick={() => copy(editedTemplateLlm)}>
                <Copy size={12} className="mr-1.5" /> Copier
              </Button>
            </div>
          )}
          {editedSchemaRec && (
            <div>
              <div className="text-xs font-semibold text-blue-gray-600 mb-2">Schema.org recommandé</div>
              <textarea 
                className="w-full text-xs font-mono bg-neutral-50 rounded-lg border border-neutral-200 p-4 min-h-[150px] resize-y focus:outline-none focus:ring-1 focus:ring-accent text-blue-gray-700"
                value={editedSchemaRec}
                onChange={(e) => setEditedSchemaRec(e.target.value)}
              />
              <Button size="sm" variant="secondary" className="mt-2" onClick={() => copy(editedSchemaRec)}>
                <Copy size={12} className="mr-1.5" /> Copier
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}


// ── Main component ─────────────────────────────────────────────────────────

export default function ProjectPosition() {
  const { geminiConfigured, project, setPositionAnalyseButton } = useOutletContext();
  const location = useLocation();
  const slug = project?.slug;
  const { showToast } = useToast();

  const { iaData, loading: iaLoading, error: iaError, saveSuccess, fetchAnalysis, saveAnalysis } = usePositioningAnalysis(slug);

  // Si on arrive depuis la recherche, naviguer vers le bon onglet
  const highlight = location.state?.searchHighlight || null;
  const highlightPosKey = location.state?.posKey || null;

  const posKeyToTab = {
    mots_cles: 'identite',
    concurrents: 'concurrence',
    todo_list: 'action',
    recommandations: 'action',
  };
  const initialTab = highlightPosKey ? (posKeyToTab[highlightPosKey] || 'overview') : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [lastTokenUsage, setLastTokenUsage] = useState(null);

  useEffect(() => { if (slug) fetchAnalysis(); }, [slug, fetchAnalysis]);

  const handleIaAnalyse = useCallback(async () => {
    if (!slug || !project) return;
    try {
      // Les pages de référence sont maintenant gérées et sauvegardées directement sur l'objet 'project'
      const result = await saveAnalysis({ reference_pages: project.reference_pages || [], reference_pages_data: [] });
      if (result?.raw_json?.token_usage) {
        setLastTokenUsage(result.raw_json.token_usage);
        const usage = result.raw_json.token_usage;
        showToast(`Analyse GEO terminée — ${usage.total?.toLocaleString()} tokens (≈ ${usage.estimated_cost_eur?.toFixed(5)}€)`, 'success');
      }
    } catch {
      showToast('Erreur analyse GEO', 'error');
    }
  }, [slug, project, saveAnalysis, showToast]);

  useEffect(() => {
    if (!setPositionAnalyseButton) return;
    setPositionAnalyseButton({
      label: "Lancer l'analyse GEO",
      onClick: handleIaAnalyse,
      disabled: !geminiConfigured,
      loading: iaLoading
    });
    return () => setPositionAnalyseButton(null);
  }, [setPositionAnalyseButton, geminiConfigured, iaLoading, handleIaAnalyse]);

  const tabs = [
    { key: 'overview',    label: "Vue d'ensemble" },
    { key: 'identite',    label: 'Identité & Mots-clés' },
    { key: 'scores',      label: 'Scores GEO' },
    { key: 'concurrence', label: 'Concurrence' },
    { key: 'action',      label: "Plan d'action" },
    { key: 'ressources',  label: 'Ressources' },
  ];


  if (!project) return <Loader label="Chargement..." />;

  return (
    <div className="w-full mx-auto px-2 md:px-0 flex flex-col gap-6">
      <SearchHighlightBanner />
      <ReferencePagesBanner project={project} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-blue-gray-900 flex items-center gap-2">
            <Brain size={20} className="text-accent" /> Positionnement GEO
          </h1>
          <p className="text-sm text-blue-gray-400 mt-0.5">Analyse de visibilité dans les moteurs de recherche génératifs (LLM)</p>
        </div>
        {iaData?.raw_json?.identite?.secteur && (
          <div className="flex items-center gap-2">
            <Badge color="accent">{iaData.raw_json.identite.secteur}</Badge>
            {iaData.raw_json.identite?.localite && <Badge color="gray">{iaData.raw_json.identite.localite}</Badge>}
          </div>
        )}
      </div>

      {!geminiConfigured && (
        <Card className="bg-orange-50 border-orange-200">
          <div className="flex items-center gap-3">
            <Lightbulb className="text-orange-500 shrink-0" size={18} />
            <div>
              <div className="font-semibold text-orange-900 text-sm">Clé Gemini requise</div>
              <a href="/profile" className="text-sm text-orange-700 hover:underline">Configurer dans le profil →</a>
            </div>
          </div>
        </Card>
      )}

      {iaLoading && !iaData && <AnalyseSkeleton />}
      {iaError && <Card className="bg-red-50 border-red-200"><p className="text-red-700 text-sm">{iaError}</p></Card>}

      {!iaLoading && !iaData && !iaError && (
        <Card className="text-center py-16">
          <Brain size={40} className="mx-auto text-blue-gray-200 mb-4" />
          <p className="text-blue-gray-500 mb-1">Aucune analyse GEO disponible</p>
          <p className="text-blue-gray-400 text-sm">Lancez l'analyse GEO depuis le bouton en haut à droite</p>
        </Card>
      )}

      {iaData && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto bg-white/40 backdrop-blur-sm border border-white/70 rounded-xl p-1">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === t.key ? 'bg-white text-blue-gray-900 shadow-sm' : 'text-blue-gray-500 hover:text-blue-gray-700'
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'overview'    && <TabOverview iaData={iaData} setActiveTab={setActiveTab} />}
          {activeTab === 'identite'    && <TabIdentite iaData={iaData} />}
          {activeTab === 'scores'      && <TabScoresGeo iaData={iaData} slug={slug} showToast={showToast} />}
          {activeTab === 'concurrence' && <TabConcurrence iaData={iaData} />}
          {activeTab === 'action'      && <TabAction iaData={iaData} slug={slug} />}
          {activeTab === 'ressources'  && <TabRessources iaData={iaData} project={project} slug={slug} showToast={showToast} />}


        </>
      )}
    </div>
  );
}
